import React, {useCallback, useEffect, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import {QRCodeSVG} from "qrcode.react";
import {useTranslation} from "react-i18next";
import {Preloader} from "components/Preloader";
import {getBankIDStatus} from "helpers/helper";

export const ModalIdentifyContractPerson = inject("store")(
  observer(({store: {auth, buyer}}) => {
    const {t} = useTranslation();
    const {qrCodeData, autoStartToken} = auth;
    const {currentRequest} = buyer;
    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);
    // FIX: countdown was stored in a local variable, never cleaned up on unmount (memory leak)
    const countdownRef = useRef(null);
    const userMessage = auth.bankIdVerifyData?.userMessage;
    const COUNTDOWN_START = 300;
    const [timeLeft, setTimeLeft] = useState(COUNTDOWN_START);

    useEffect(() => {
      auth.getBankIdDataOnContract();
      intervalRef.current = setInterval(() => {
        auth.getBankIdQRCodeOnContract();
      }, 1000);

      countdownRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);

            auth.cancelAuthBankId();
            auth.setBankIdVerifyData({});
            auth.updateAuthModalName(null);

            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }, [auth]);

    useEffect(() => {
      if (auth.bankIdVerifyData?.hintCode === "startFailed") {
        auth.getBankIdDataOnContract();

        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
          auth.getBankIdQRCodeOnContract();
        }, 1000);
      }
    }, [auth, auth.bankIdVerifyData?.hintCode]);

    // FIX: stop QR polling and countdown as soon as BankID completes.
    // Without this, the 1s polling kept firing after completion, and each poll's
    // setBankIdVerifyData(data) (without id) overwrote the confirm response (with id),
    // preventing the modal from ever closing — causing infinite loading.
    // Modal close + success toast are now handled solely by setBankIdVerifyData in
    // auth.store when the /confirm endpoint responds with id.
    useEffect(() => {
      if (auth.bankIdVerifyData?.status === "complete" || auth.bankIdVerifyData?.id) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
      }
    }, [auth.bankIdVerifyData?.status, auth.bankIdVerifyData?.id]);

    const checkBankIdStatus = useCallback(() => {
      if (auth.bankIdVerifyData?.id) {
        return null;
      }

      switch (auth.bankIdVerifyData?.status) {
        case "failed":
          if (auth.bankIdVerifyData?.hintCode === "startFailed") {
            return getBankIDStatus(userMessage);
          }

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          timeoutRef.current = setTimeout(() => {
            auth.updateAuthModalName(null);
            auth.setBankIdVerifyData({});
          }, 3000);

          return getBankIDStatus(userMessage);
        case "complete":
          return null;

        default:
          return getBankIDStatus(userMessage);
      }
    }, [auth, userMessage]);

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    return (
      <div className="modal-auth modal-identify_wrapper">
        <div className="modal-identify_image">
          {qrCodeData ? (
            <div>
              <button
                style={{border: "none", background: "none"}}
                onClick={() =>
                  document
                    .querySelector(".modal-ent__wrapper")
                    .classList.add("fullscreen")
                }
              >
                <QRCodeSVG value={qrCodeData} size={180} />
              </button>
              <div className="modal-identify_timer">
                {t("TIME_LEFT")}: {formatTime(timeLeft)}
              </div>
            </div>
          ) : (
            <Preloader addClass="hght-210 df-row-jcc-center" />
          )}
        </div>
        <p className="modal-identify_text mb-0">{checkBankIdStatus()}</p>

        {qrCodeData && (
          <a
            href={`bankid:///?autostarttoken=${autoStartToken}&redirect=${window.location.origin}/requests/${currentRequest.id}`}
            className="modal-identify_link mb-12"
          >
            {t("OPEN_BANK_ID_LINK")}
          </a>
        )}
      </div>
    );
  })
);
